import type { WeaponDocument } from '../../../engine/content/schema/weapon';

/**
 * Transport-neutral persistence for Gunsmith weapon documents, mirroring the
 * shape of `src/engine/editor/persistence/ProjectPersistence.ts`'s
 * `MapPersistence`/`PosePersistence` (own injection point rather than
 * extending that file directly, since `src/engine/editor/persistence/` is
 * outside this programme's assigned paths).
 *
 * A creator's weapons are a roster - independently named, saved and
 * published items, not one always-current document (see
 * contracts/studio-editor-studio-api.md) - so persistence operates per-item,
 * not on a whole-document snapshot the way map persistence does.
 *
 * The host app installs a real implementation via `setWeaponPersistence` at
 * startup; until `sstk-studio-api`'s endpoint shape is settled, the dev
 * harness installs `localStorageWeaponPersistence` (see dev/) so the full
 * save/undo/publish flow is real and verifiable end to end, not stubbed out.
 */

export interface WeaponPersistenceResult {
  weapons: WeaponDocument[];
  /** Ids currently live (published at least once and not since unpublished). */
  publishedWeaponIds: string[];
}

export interface WeaponPublishResult {
  published: true;
  publishedAt: string;
}

/**
 * Thrown by `publishWeapon` when the server's publish-validation gate
 * rejects the saved draft (sstk-studio-api's 422, `{"detail":{"message",
 * "errors":[...]}}`) - a distinct type from a generic network/auth failure
 * so the UI can show exactly which rules blocked publish, not just "failed".
 */
export class WeaponPublishValidationError extends Error {
  readonly errors: string[];
  constructor(message: string, errors: string[]) {
    super(message);
    this.name = 'WeaponPublishValidationError';
    this.errors = errors;
  }
}

export interface WeaponPersistence {
  /** List every weapon a creator has authored for this project. */
  listWeapons(projectId: string): Promise<WeaponPersistenceResult>;
  /** Upsert one weapon by id, unconditionally (no publish-gate validation). Returns the saved document. */
  saveWeapon(projectId: string, weapon: WeaponDocument): Promise<WeaponDocument>;
  /**
   * Publish an already-saved weapon, making it live. Runs the server's
   * publish-validation gate; throws `WeaponPublishValidationError` if it
   * rejects the saved draft.
   */
  publishWeapon(projectId: string, weaponId: string): Promise<WeaponPublishResult>;
  /**
   * Delete an already-saved weapon (draft or published, no distinction -
   * the server hard-deletes the row either way). Rejects if the weapon does
   * not exist.
   */
  deleteWeapon(projectId: string, weaponId: string): Promise<void>;
}

let activeWeaponPersistence: WeaponPersistence | null = null;

/** Install the weapon persistence implementation. Pass `null` to clear. */
export function setWeaponPersistence(impl: WeaponPersistence | null): void {
  activeWeaponPersistence = impl;
}

export function getWeaponPersistence(): WeaponPersistence {
  if (!activeWeaponPersistence) {
    throw new Error('Weapon persistence is not configured. Call setWeaponPersistence(impl) at startup.');
  }
  return activeWeaponPersistence;
}
